import * as THREE from 'three'
import { viewport, onResize } from '../core/viewport.js'
import { params } from '../config/persistence.js'
import particlesVertexShader from '../shaders/particles/vertex.glsl'
import particlesFragmentShader from '../shaders/particles/fragment.glsl'

export const colorFromHex = (hex) => {
    const c = new THREE.Color(hex)
    return new THREE.Vector3(c.r, c.g, c.b)
}

export const initParticles = (scene, compute, particleCount, plyColorData) => {
    const particleSystem = {}

    const particlesUvArray = new Float32Array(particleCount * 2)
    const randomSizes = new Float32Array(particleCount)

    for (let y = 0; y < compute.size; y++) {
        for (let x = 0; x < compute.size; x++) {
            const i = (y * compute.size + x)
            const i2 = i * 2
            particlesUvArray[i2 + 0] = (x + 0.5) / compute.size
            particlesUvArray[i2 + 1] = (y + 0.5) / compute.size
            randomSizes[i] = Math.random()
        }
    }

    particleSystem.geometry = new THREE.BufferGeometry()
    particleSystem.geometry.setDrawRange(0, particleCount)
    particleSystem.geometry.setAttribute('aParticlesUv', new THREE.BufferAttribute(particlesUvArray, 2))
    particleSystem.geometry.setAttribute('aSize', new THREE.BufferAttribute(randomSizes, 1))

    // Per-particle color texture from PLY data
    let colorTexture
    const usePerParticleColor = plyColorData !== null
    if (usePerParticleColor) {
        const colorTextureData = new Float32Array(compute.size * compute.size * 4)
        for (let i = 0; i < particleCount; i++) {
            colorTextureData[i * 4 + 0] = plyColorData[i * 3 + 0]
            colorTextureData[i * 4 + 1] = plyColorData[i * 3 + 1]
            colorTextureData[i * 4 + 2] = plyColorData[i * 3 + 2]
            colorTextureData[i * 4 + 3] = 1.0
        }
        colorTexture = new THREE.DataTexture(colorTextureData, compute.size, compute.size, THREE.RGBAFormat, THREE.FloatType)
        colorTexture.needsUpdate = true
    } else {
        colorTexture = new THREE.DataTexture(new Float32Array(4), 1, 1, THREE.RGBAFormat, THREE.FloatType)
        colorTexture.needsUpdate = true
    }

    particleSystem.material = new THREE.ShaderMaterial({
        vertexShader: particlesVertexShader,
        fragmentShader: particlesFragmentShader,
        transparent: true,
        depthWrite: false,
        blending: THREE.CustomBlending,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
        blendSrcAlpha: THREE.OneFactor,
        blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
        uniforms: {
            uSize: new THREE.Uniform(params.particleSize),
            uResolution: new THREE.Uniform(new THREE.Vector2(viewport.width * viewport.pixelRatio, viewport.height * viewport.pixelRatio)),
            uParticlesTexture: new THREE.Uniform(),
            uColorTexture: new THREE.Uniform(colorTexture),
            uUsePerParticleColor: new THREE.Uniform(usePerParticleColor ? 1.0 : 0.0),
            uColor: new THREE.Uniform(colorFromHex(params.particleColor)),
            uColorShift: new THREE.Uniform(colorFromHex(params.colorShift)),
            uTime: new THREE.Uniform(0),
            uOpacityVariance: new THREE.Uniform(params.opacityVariance),
            uColorVariance: new THREE.Uniform(params.colorVariance),
            uSizeVariance: new THREE.Uniform(params.sizeVariance),
            uFlashIntensity: new THREE.Uniform(params.flashIntensity),
            uDimIntensity: new THREE.Uniform(params.dimIntensity),
            uParticleShape: new THREE.Uniform(params.particleShape),
            uShapeSoftness: new THREE.Uniform(params.shapeSoftness),
            uDofFocus: new THREE.Uniform(params.dofEnabled ? params.dofFocus : 0.0),
            uDofAperture: new THREE.Uniform(params.dofEnabled ? params.dofAperture : 0.0),
            uDofMaxBlur: new THREE.Uniform(params.dofEnabled ? params.dofMaxBlur : 0.0),
            uSaturation: new THREE.Uniform(params.saturation !== undefined ? params.saturation : 1.0),
            uGlobalFade: new THREE.Uniform(1.0),
            uFadeTopDown: new THREE.Uniform(0.0),
            uMouseWorld: new THREE.Uniform(new THREE.Vector3(9999, 9999, 9999)),
            uRevealRadius: new THREE.Uniform(params.revealEnabled ? params.revealRadius : 0.0),
            uRevealSoftness: new THREE.Uniform(params.revealSoftness),
        }
    })

    particleSystem.points = new THREE.Points(particleSystem.geometry, particleSystem.material)
    scene.add(particleSystem.points)

    onResize((vp) => {
        if (particleSystem.material) {
            const res = new THREE.Vector2(vp.width * vp.pixelRatio, vp.height * vp.pixelRatio)
            particleSystem.material.uniforms.uResolution.value.copy(res)
            if (window._splatMaterial)
                window._splatMaterial.uniforms.uResolution.value.copy(res)
        }
    })

    return particleSystem
}
